import { Children, Component } from 'react';
import { debounceInAnimationFrame } from '@optimus-web/common';
import { Alignment, Virtual } from '@optimus-web/helpers';
import { List } from '../List';
import { EmptyMessage } from './style';

export interface SelectListProps {
    selectedIndex?: number;
    virtualized?: boolean;
    defaultItemHeight: number;
    maxHeight: number;
    children?: React.ReactNode;
    emptyMessage?: React.ReactNode;
    keyboardHandled?: boolean;
}

export type ISelectList = Pick<SelectList, 'updateMaxHeight'>;

class SelectList extends Component<SelectListProps> {
    static defaultProps = {
        emptyMessage: 'No value',
    };

    private virtualSizer: HTMLElement | null = null;

    private childrenHeight = Number.POSITIVE_INFINITY;
    private overlayMaxHeight = Number.POSITIVE_INFINITY;

    componentDidUpdate(prevProps: SelectListProps) {
        if (this.props.virtualized) {
            const childrenCount = Children.count(this.props.children);
            const prevChildrenCount = Children.count(prevProps.children);

            // When removing filter value, rerender is required in Virtual list to recompute proper height
            if (childrenCount > prevChildrenCount) {
                window.setTimeout(() => {
                    this.forceUpdate();
                }, 0);
            }
        }
    }

    updateMaxHeight = (maxHeight: number) => {
        this.overlayMaxHeight = maxHeight;
        this.changeCssHeight();
    };

    handleScrollableSizeChange = (size: number) => {
        this.childrenHeight = size;
        this.changeCssHeight();
    };

    changeCssHeight = debounceInAnimationFrame(() => {
        if (this.props.virtualized && this.virtualSizer) {
            const height = Math.min(this.props.maxHeight, this.overlayMaxHeight, this.childrenHeight);
            this.virtualSizer.style.height = `${height}px`;
        }
    });

    handleVirtualSizerRef = (c: HTMLElement | null) => (this.virtualSizer = c);

    render() {
        const { virtualized, maxHeight, children, emptyMessage, ...other } = this.props;

        let content;
        const childrenCount = Children.count(children);
        if (childrenCount === 0) {
            content = <EmptyMessage>{emptyMessage}</EmptyMessage>;
        } else if (virtualized) {
            const { defaultItemHeight, selectedIndex } = this.props;
            const height = Math.min(maxHeight, this.overlayMaxHeight, this.childrenHeight);

            const virtualProps = {
                defaultItemHeight,
                currentIndex: selectedIndex,
                scrollToAlignment: Alignment.Center,
                shouldScrollToIndex: this.props.keyboardHandled,
                containerSize: height,
                onScrollableSizeChange: this.handleScrollableSizeChange,
            };

            content = (
                <div style={{ height }} ref={this.handleVirtualSizerRef}>
                    <Virtual {...virtualProps}>{children}</Virtual>
                </div>
            );
        } else {
            content = children;
        }

        return (
            <List {...other} role="listbox">
                {content}
            </List>
        );
    }
}

export default SelectList;
